import { Box, MenuItem } from '@mui/material';
import {
  CustomTextField,
  DialogBody,
  CustomSelect,
  CustomCheckbox,
  CustomTextArea,
} from '../../../../../common/components';
import { HorizontalForm } from '../../../../../common';
import { formatDateForInput, isBooleanActive } from '../../../utils/contractsUtils';

const GeneralDataTab = ({
  localContract,
  isArchived,
  isReadOnly = false,
  handleInputChange,
  handleSelectChange,
  plans,
  groups,
  paymentMethods,
  legalEntities,
  constructionTypes,
  preAdjReasons,
}) => {
  const filteredLegalEntities = legalEntities.filter((le) => le.is_intermediate_entity === '1');
  
  return (
    <DialogBody>
      <HorizontalForm withBorder={false} sx={{ p: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mr: 2 }}>
          <CustomTextField
            margin="dense"
            name="contract_number"
            label="Contrato N°"
            fullWidth
            disabled
            value={localContract.contract_number || ''}
          />
          <CustomTextField
            margin="dense"
            name="plan_name"
            label="Plan"
            fullWidth
            disabled
            value={plans.find((plan) => plan.uuid === localContract.uuid_plan)?.name || '-'}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mr: 2 }}>
          <CustomTextField
            margin="dense"
            name="group_name"
            label="Grupo"
            fullWidth
            disabled
            value={groups.find((group) => group.uuid === localContract.uuid_group)?.name || '-'}
          />
          <CustomSelect
            name="uuid_payment_method"
            label="Forma Pago"
            value={localContract.uuid_payment_method || ''}
            onChange={handleSelectChange}
            disabled={isReadOnly}
            sx={{ marginTop: 2.5 }}
          >
            {paymentMethods.map((pm) => (
              <MenuItem key={pm.uuid} value={pm.uuid}>
                {pm.name}
              </MenuItem>
            ))}
          </CustomSelect>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mr: 2 }}>
          <CustomTextField
            margin="dense"
            name="first_fee"
            label="Cuota comienzo"
            fullWidth
            value={localContract.first_fee || ''}
            onChange={handleInputChange}
            disabled={isReadOnly}
          />
          <CustomTextField
            margin="dense"
            name="request_number"
            label="Número solicitud"
            fullWidth
            value={localContract.request_number || ''}
            onChange={handleInputChange}
            disabled={isReadOnly}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <CustomSelect
            name="uuid_legal_entity"
            label="Entidad Intermedia"
            value={localContract.uuid_legal_entity || ''}
            onChange={handleSelectChange}
            disabled={isReadOnly}
            sx={{ marginTop: 2.5 }}
          >
            <MenuItem value="">Sin entidad legal</MenuItem>
            {filteredLegalEntities.map((le) => (
              <MenuItem key={le.uuid} value={le.uuid}>
                {le.legal_business_name}
              </MenuItem>
            ))}
          </CustomSelect>
          <CustomSelect
            name="uuid_construction_type"
            label="Tipo de construcción"
            value={localContract.uuid_construction_type || 'None'}
            onChange={handleSelectChange}
            disabled={isReadOnly}
            sx={{ marginTop: 3 }}
          >
            <MenuItem value="None">Ninguno</MenuItem>
            {constructionTypes.map((ct) => (
              <MenuItem key={ct.uuid} value={ct.uuid}>
                {ct.name}
              </MenuItem>
            ))}
          </CustomSelect>
        </Box>
      </HorizontalForm>

      <HorizontalForm withBorder={false} sx={{ p: 1, pt: 0, mt: -1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mr: 2 }}>
          <CustomTextField
            margin="dense"
            name="initial_subscription_date"
            label="Fecha Suscripción"
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={formatDateForInput(localContract.initial_subscription_date)}
            onChange={handleInputChange}
            disabled={isReadOnly}
          />
          <CustomTextField
            margin="dense"
            name="current_possessor_date"
            label="Fecha Poseedor"
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={formatDateForInput(localContract.current_possessor_date)}
            onChange={handleInputChange}
            disabled={isReadOnly}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mr: 2 }}>
          <CustomTextField
            margin="dense"
            name="adjudication_date"
            label="Fecha adjudicación"
            type="date"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={formatDateForInput(localContract.adjudication_date)}
            onChange={handleInputChange}
            disabled={isReadOnly}
          />
          <CustomTextField
            margin="dense"
            name="life_insurance"
            label="Seguro de vida ($)"
            fullWidth
            value={localContract.life_insurance || ''}
            onChange={handleInputChange}
            disabled={isReadOnly}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, mr: 2 }}>
          <CustomCheckbox
            label="Cupón por mail"
            disabled={isReadOnly}
            checked={isBooleanActive(localContract.by_email)}
            onChange={(e) =>
              handleInputChange({
                target: { name: 'by_email', value: e.target.checked },
              })
            }
            sx={{
              mt: 3,
              justifyContent: 'center',
            }}
          />
          <CustomCheckbox
            disabled={isReadOnly}
            label="Lote propio&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"
            checked={isBooleanActive(localContract.private_land)}
            onChange={(e) =>
              handleInputChange({
                target: { name: 'private_land', value: e.target.checked },
              })
            }
            sx={{
              justifyContent: 'center',
            }}
          />
        </Box>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CustomCheckbox
            disabled={isReadOnly}
            label="Es plan premium"
            checked={isBooleanActive(localContract.is_premium)}
            onChange={(e) =>
              handleInputChange({
                target: { name: 'is_premium', value: e.target.checked },
              })
            }
            sx={{
              mt: 3,
              justifyContent: 'center',
            }}
          />
          <CustomCheckbox
            label="No generar&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;"
            disabled={isReadOnly}
            checked={isBooleanActive(localContract.not_generate)}
            onChange={(e) =>
              handleInputChange({
                target: { name: 'not_generate', value: e.target.checked },
              })
            }
            sx={{
              justifyContent: 'center',
            }}
          />
        </Box>
      </HorizontalForm>

      <HorizontalForm withBorder={false} sx={{ p: 1, pt: 0 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mr: 2 }}>
          <CustomSelect
            name="uuid_pre_adjudication_reason"
            label="Motivo Pre-adjudicación"
            value={localContract.uuid_pre_adjudication_reason || ''}
            onChange={handleSelectChange}
            disabled={isReadOnly}
            sx={{ marginTop: 2 }}
          >
            {preAdjReasons.map((par) => (
              <MenuItem key={par.uuid} value={par.uuid}>
                {par.name}
              </MenuItem>
            ))}
          </CustomSelect>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', mt: 0.5 }}>
          <CustomTextArea
            placeholder="Dirección"
            name="construction_site_location"
            value={localContract.construction_site_location || ''}
            onChange={handleInputChange}
            disabled={isReadOnly}
          />
        </Box>
      </HorizontalForm>

      {isArchived && (
        <HorizontalForm withBorder={false} sx={{ p: 1, pt: 0 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mr: 4 }}>
            <CustomTextField
              margin="dense"
              name="deleted_at"
              label="Fecha Liberación"
              type="date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={formatDateForInput(localContract.deleted_at)}
              disabled
            />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CustomTextField
              margin="dense"
              name="release_reason_obs"
              label="Motivo Liberación"
              fullWidth
              value={localContract.release_reason_obs || ''}
              disabled
            />
          </Box>
        </HorizontalForm>
      )}
    </DialogBody>
  );
};

export default GeneralDataTab;
